import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { authenticatedFetch } from '../lib/api-client';
import { queryKeys } from '../lib/query-client';

/**
 * Invitation Types
 */
export interface Invitation {
  email: string;
  activationCode: string;
  status: 'pending' | 'activated';
  createdAt: string;
  activatedAt?: string;
  userId?: string;
}

export interface CreateInvitationResponse {
  email: string;
  activationCode: string;
  createdAt: string;
}

export interface InvitationsResponse {
  invitations: Invitation[];
}

/**
 * User Types
 */
export interface AdminUser {
  userId: string;
  email: string;
  preferredName?: string;
  familyName?: string;
  roles: string[];
  status: string;
  createdAt: string;
  updatedAt: string;
  metadata: {
    onboardingCompleted: boolean;
  };
}

export interface UsersResponse {
  users: AdminUser[];
}

/**
 * useInvitations Hook
 *
 * Fetches all invitations from the admin API.
 * Returns invitations sorted by createdAt (newest first).
 *
 * @example
 * ```tsx
 * function InvitationsTable() {
 *   const { data, isLoading, error } = useInvitations();
 *
 *   if (isLoading) return <Spinner />;
 *   if (error) return <Error message={error.message} />;
 *
 *   return <Table data={data.invitations} />;
 * }
 * ```
 */
export function useInvitations() {
  return useQuery({
    queryKey: queryKeys.admin.invitations,
    queryFn: () =>
      authenticatedFetch<InvitationsResponse>('/internal/v1/admin/invitations'),
    staleTime: 60 * 1000, // 1 minute
  });
}

/**
 * useCreateInvitation Hook
 *
 * Mutation hook for creating a new invitation.
 * Automatically invalidates invitations query on success.
 *
 * @example
 * ```tsx
 * function InviteUserForm() {
 *   const createInvitation = useCreateInvitation();
 *
 *   async function handleSubmit(email: string) {
 *     const invitation = await createInvitation.mutateAsync(email);
 *     toast.success(`Invitation created: ${invitation.activationCode}`);
 *   }
 *
 *   return (
 *     <button
 *       onClick={() => handleSubmit('user@example.com')}
 *       disabled={createInvitation.isPending}
 *     >
 *       Create Invitation
 *     </button>
 *   );
 * }
 * ```
 */
export function useCreateInvitation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (email: string) =>
      authenticatedFetch<CreateInvitationResponse>(
        '/internal/v1/admin/invitations',
        {
          method: 'POST',
          body: JSON.stringify({ email }),
        }
      ),
    onSuccess: () => {
      // Invalidate to trigger refetch
      queryClient.invalidateQueries({ queryKey: queryKeys.admin.invitations });
    },
  });
}

/**
 * useDeleteInvitation Hook
 *
 * Mutation hook for deleting/revoking an invitation.
 * Automatically invalidates invitations query on success.
 *
 * @example
 * ```tsx
 * function InvitationRow({ invitation }) {
 *   const deleteInvitation = useDeleteInvitation();
 *
 *   async function handleDelete() {
 *     if (confirm('Are you sure?')) {
 *       await deleteInvitation.mutateAsync(invitation.email);
 *       toast.success('Invitation deleted');
 *     }
 *   }
 *
 *   return (
 *     <button
 *       onClick={handleDelete}
 *       disabled={deleteInvitation.isPending || invitation.status === 'activated'}
 *     >
 *       Delete
 *     </button>
 *   );
 * }
 * ```
 */
export function useDeleteInvitation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (email: string) =>
      authenticatedFetch<void>(
        `/internal/v1/admin/invitations/${encodeURIComponent(email)}`,
        {
          method: 'DELETE',
        }
      ),
    onSuccess: () => {
      // Invalidate to trigger refetch
      queryClient.invalidateQueries({ queryKey: queryKeys.admin.invitations });
    },
  });
}

/**
 * useUsers Hook
 *
 * Fetches all users from the admin API.
 * Returns users sorted by createdAt (newest first).
 *
 * @example
 * ```tsx
 * function UsersTable() {
 *   const { data, isLoading, error } = useUsers();
 *
 *   if (isLoading) return <Spinner />;
 *   if (error) return <Error message={error.message} />;
 *
 *   return <Table data={data.users} />;
 * }
 * ```
 */
export function useUsers() {
  return useQuery({
    queryKey: queryKeys.admin.users,
    queryFn: () =>
      authenticatedFetch<UsersResponse>('/internal/v1/admin/users'),
    staleTime: 60 * 1000, // 1 minute
  });
}
